import { Node, mergeAttributes, nodeInputRule } from '@tiptap/core';
import { Image, inputRegex } from '@tiptap/extension-image'
//import { Plugin, PluginKey } from 'prosemirror-state';
import { EditorState, Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from 'prosemirror-view';
import axios from 'axios';
import { resizeImage } from './resize-image'
import { browserImageReader } from './browser-image-reader';
import { uid } from 'uid';

const mime = require('mime-types')


let fileQueue = {};

const CustomImage = Node.create({
  name: 'customImage',

  addOptions () {
    return {
      resizeMaxWidth: 600,
      resizeMaxHeight: 300,
      uploadUrl: '/files/upload',
      uploadedPath: '/client_uploads/filepond_image_uploads',
      orderId: 0,
      inline: false,
    }
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: match => {
          const [,, alt, src, title] = match

          return { src, alt, title }
        },
      }),
    ]
  },

  inline() {
    return this.options.inline
  },

  group() {
    return this.options.inline ? 'inline' : 'block'
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      uploading: {
        default: false,
      },
      id: {
        default: 0
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (dom) => ({
          src: dom.getAttribute('src'),
          alt: dom.getAttribute('alt'),
          title: dom.getAttribute('title'),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    console.log('rendering')
    return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
  },

  addCommands() {
    return {
      setImage: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },

  addNodeView() {

    const orderId = this.options.orderId;
    const editor = this.editor;
    const options = this.options;

    const upload = async (file) => {
      try {
        const formData = new FormData();
        const abortController = new AbortController();

        // fieldName is the name of the input field
        // file is the actual file object to send
        formData.append('files[]', file, file.name);
        formData.append('orderfiletype', 'image');
        formData.append('orderid', orderId);

        const response = await axios({
          method: 'POST',
          url: this.options.uploadUrl,
          data: formData,
          signal: abortController.signal,
        });

        return response;

      } catch (e) {
        console.error('failed to upload image', e);
      }
    };

    return ({ node, view }) => {
        const imgWrapper = document.createElement('p');
        const img = document.createElement('img');
        const file = fileQueue[node.attrs.id];

        //img.src = node.attrs.uploading ? this.options.placeholder : node.attrs.src;
        img.src = node.attrs.src;
        img.alt = node.attrs.alt;
        img.title = node.attrs.title;

        //@TODO - if file failed to upload because the user refreshed their browser
        //or some other error occured.  Retry the file upload.
        //right now any upload failures will be saved as data images


        if (node.attrs.uploading && file) {
          // Add an uploading indicator. Customize this to your needs.
          //img.style.border = '2px dashed #ccc'

          imgWrapper.classList.add("custom-image-wrapper");
          imgWrapper.dataset.uploading = true;

          upload(file).then(response => {
            const uploadedImage = response.data.files[0];
            const {mime_type, id, filename} = uploadedImage;
            const extension = mime.extension(mime_type);

            imgWrapper.classList.remove('custom-image-wrapper');

            editor.commands.updateAttributes('customImage', {
              src: `${options.uploadedPath}/${orderId}/${id}.${extension}`,
              uploading: false
            })

            delete fileQueue[node.attrs.id];
          }).catch(response => {
            //@TODO need error handling

            imgWrapper.classList.add('image-upload-failed');
          })
        }

        imgWrapper.append(img);

        return {
          dom: imgWrapper,
          contentDom: img,
          update: (updatedNode) => {
            if (!updatedNode.attrs.src) return;

            //img.src = updatedNode.attrs.uploading ? this.options.placeholder : updatedNode.attrs.src;
            img.src = updatedNode.attrs.src;
            img.alt = updatedNode.attrs.alt;
            img.title = updatedNode.attrs.title;

            if (updatedNode.attrs.uploading) {
              img.style.border = '2px dashed #ccc';
            } else {
              img.style.border = '';
            }

            return true;
          },
        };
    };
  },

  addProseMirrorPlugins () {

    const nodeType = this;

    const setImageToView = (src, attrs) => {
      const editor = this.editor;

      return editor.commands.setImage({
        ...attrs,
        src,
      });
    }

    const uploadImage = async (file, schema, view) => {
      const editor = this.editor;

      let {
        resizeMaxHeight,
        resizeMaxWidth,
        imageReader
      } = this.options;

      if(!imageReader) {
        imageReader = browserImageReader
      }

      //get base64 image from reader
      const img = await imageReader(file);

      //resize the image
      const src = await resizeImage(img, resizeMaxWidth, resizeMaxHeight);

      const imageId = uid();

      try {
        fileQueue[imageId] = file;

        let imageView = setImageToView(src, {
          uploading: true,
          alt: 'uploading',
          id: imageId
        });
      } catch (e) {
        console.error('upload image error:', e.message)
      }
    }

    return [
      new Plugin({
        key: new PluginKey('eventHandler'),
        props: {
          handleDrop(view, event, slice) {
            const { files } = event.dataTransfer;
            const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
            const { schema } = view.state

            if (imageFiles) {
              event.preventDefault();

              imageFiles.forEach(imageFile => uploadImage(imageFile, schema, view));
            }
          },

          handlePaste(view, event, slice) {
            console.log('the event', event)
            const { files } = event.clipboardData;
            const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
            const { schema } = view.state

            console.log('soemtjhing pasted', imageFiles);

            if (imageFiles) {
              event.preventDefault();

              imageFiles.forEach(imageFile => uploadImage(imageFile, schema, view));
            }
          },
        }
      })
    ]
  },

  onDestroy() {
    // @TODO - delete the image from DB if user removes it from editor.
  },
});

export default CustomImage;
